/**
 * Luật thuần của màn Báo cáo lỗi & Sự cố (`run-error.ts`, `incident-errors.ts` và phần gom số của
 * `RpaIncidentsView.tsx` ở web-portal native): rút gọn lỗi, xếp mức độ, gom theo lỗi, ngày, giờ, Project và nguồn.
 */
import type { LoaiLoi, MucDo, NguonSuCo, SuCo } from '../types';

// ─────────────── Lỗi rút gọn ───────────────

const DAI_NGAN = 60;

export type MaLoiChay =
  | 'elementNotFound'
  | 'nullReference'
  | 'nullValue'
  | 'missingRead'
  | 'unknownWorkflow'
  | 'unknownQueue'
  | 'channelClosed'
  | 'invokeFailed'
  | 'licenseNotFound'
  | 'databaseConnection'
  | 'libraryLoad'
  | 'userStopped'
  | 'accessDenied'
  | 'timeout';

export const MA_LOI_CHAY: readonly MaLoiChay[] = [
  'elementNotFound', 'nullReference', 'nullValue', 'missingRead', 'unknownWorkflow', 'unknownQueue', 'channelClosed',
  'invokeFailed', 'licenseNotFound', 'databaseConnection', 'libraryLoad', 'userStopped', 'accessDenied', 'timeout',
];

/**
 * Mẫu đầu tiên khớp thắng. Lỗi OpenRPA hay lồng nhiều tầng (`Query failed: ServerError("Timeout")`), nên mẫu cụ thể
 * (Unknown workflow, ODBC…) đứng trước mẫu chung `timeout`.
 */
const MAU: ReadonlyArray<readonly [RegExp, MaLoiChay]> = [
  [/Failed locating|ElementNotFound/i, 'elementNotFound'],
  [/Object reference not set|NullReferenceException/i, 'nullReference'],
  [/Value cannot be null|ArgumentNullException/i, 'nullValue'],
  [/missing read permission/i, 'missingRead'],
  [/Unknown workflow/i, 'unknownWorkflow'],
  [/Unknown queue|bad queue/i, 'unknownQueue'],
  [/channel closed/i, 'channelClosed'],
  [/OPENRPA_INVOKE_FAILED/, 'invokeFailed'],
  [/LICENSE_NOT_FOUND|Không tìm thấy License/i, 'licenseNotFound'],
  [/ODBC|OdbcException|Can't connect to MySQL|SqlException/i, 'databaseConnection'],
  [/Failed to load library|Duplicate type name/i, 'libraryLoad'],
  [/User clicked stop/i, 'userStopped'],
  [/access denied/i, 'accessDenied'],
  [/timeout|timed out|thời gian cho phép|thời gian chờ/i, 'timeout'],
];

export type LoiNgan = { ma: MaLoiChay } | { chu: string };

/** Bỏ lớp vỏ `Query failed: ServerError("…")` còn lại lõi của lỗi. */
function boVo(text: string): string {
  const trong = /^Query failed:\s*\w+\((.*)\)$/i.exec(text)?.[1] ?? text;
  return trong.replace(/^"+|"+$/g, '').replace(/\s*correlationId:\s*\S+/i, '').trim();
}

/** Lỗi một dòng cho bảng và biểu đồ: mã đã biết (nơi gọi dịch), không thì câu lõi cắt còn 60 ký tự. */
export function loiNgan(raw: string | null | undefined): LoiNgan | null {
  if (!raw) return null;
  const text = raw.replace(/\\"/g, '"').replace(/\\r\\n|\\n/g, ' ').replace(/\s+/g, ' ').trim();
  if (!text) return null;
  const khop = MAU.find(([re]) => re.test(text));
  if (khop) return { ma: khop[1] };
  const message = [...text.matchAll(/"Message"\s*:\s*"([^"]+)"/gi)];
  const loi = message[message.length - 1]?.[1] ?? boVo(text);
  return { chu: loi.length > DAI_NGAN ? `${loi.slice(0, DAI_NGAN - 1)}…` : loi };
}

// ─────────────── Mức độ, loại, nhãn ───────────────

/** Nặng trước. */
export const MUC_DO_SU_CO: readonly MucDo[] = ['critical', 'high', 'medium', 'low'];

const MUC_THEO_MA: Record<MaLoiChay, MucDo> = {
  databaseConnection: 'critical',
  libraryLoad: 'critical',
  channelClosed: 'critical',
  unknownQueue: 'critical',
  licenseNotFound: 'high',
  unknownWorkflow: 'high',
  missingRead: 'high',
  accessDenied: 'high',
  invokeFailed: 'high',
  timeout: 'medium',
  elementNotFound: 'medium',
  nullReference: 'medium',
  nullValue: 'medium',
  userStopped: 'low',
};

const BI_HUY = ['aborted', 'cancelled'];

/**
 * Mức độ sự cố: lỗi Robot báo về đã có mức theo danh mục lỗi; nguồn khác xếp theo nội dung lỗi rồi nguồn lỗi. Lượt bị
 * huỷ là thấp, lỗi lạ là trung bình.
 */
export function mucDoSuCo(r: Pick<SuCo, 'severity' | 'message' | 'source' | 'status'>): MucDo {
  if (r.severity && (MUC_DO_SU_CO as readonly string[]).includes(r.severity)) return r.severity as MucDo;
  if (BI_HUY.includes(r.status)) return 'low';
  for (const raw of [r.message, r.source]) {
    const n = loiNgan(raw);
    if (n && 'ma' in n) return MUC_THEO_MA[n.ma];
  }
  return 'medium';
}

/** Lường trước được = lỗi đã khai ở Cấu hình lỗi; sự cố không có mã danh mục là bất ngờ. */
export function loaiSuCo(r: Pick<SuCo, 'errorKind'>): LoaiLoi {
  return r.errorKind ?? 'unforeseen';
}

/** `E-102 · NET_DNS_RESOLVE_FAILED`, như hộp Xem lỗi gọi tên lỗi; rỗng khi không có mã danh mục. */
export function nhanDanhMuc(r: Pick<SuCo, 'errorCode' | 'errorName'>): string {
  return r.errorCode ? [r.errorCode, r.errorName].filter(Boolean).join(' · ') : (r.errorName ?? '');
}

export const NGUON_SU_CO: readonly NguonSuCo[] = ['foxai', 'opencore', 'robot'];

/** Chỉ số loạt theo nguồn, cố định: màu đi theo nguồn chứ không theo thứ hạng. */
export const vtNguon = (r: Pick<SuCo, 'origin'>): number => NGUON_SU_CO.indexOf(r.origin ?? 'opencore');

// ─────────────── Lọc ───────────────

export interface BoLocSuCo {
  soNgay: number;
  project: string;
  mucDo: string;
  loai: string;
  nguon: string;
  tim: string;
}

export const KHOANG_NGAY = [7, 14, 30] as const;

const deTim = (v: string) => v.normalize('NFC').toLocaleLowerCase('vi');

const ms = (v: string | null | undefined) => {
  const t = Date.parse(v ?? '');
  return Number.isFinite(t) ? t : 0;
};

export function locSuCo(ds: readonly SuCo[], loc: BoLocSuCo, bayGio: number): SuCo[] {
  const tu = bayGio - loc.soNgay * 86_400_000;
  const tim = deTim(loc.tim.trim());
  return ds.filter(
    (r) =>
      ms(r.createdAt) >= tu &&
      (!loc.project || r.projectId === loc.project) &&
      (!loc.mucDo || mucDoSuCo(r) === loc.mucDo) &&
      (!loc.loai || loaiSuCo(r) === loc.loai) &&
      (!loc.nguon || r.origin === loc.nguon) &&
      (!tim ||
        deTim([r.name, r.workflowId, r.projectName, r.projectId, r.machineName, r.message, r.source, r.errorCode, r.errorName, r.id]
          .filter(Boolean)
          .join(' ')).includes(tim)),
  );
}

/** Project có trong dữ liệu, cho ô lọc. */
export function projectTrongSuCo(ds: readonly SuCo[]): { id: string; ten: string }[] {
  const m = new Map<string, string>();
  for (const r of ds) if (r.projectId) m.set(r.projectId, r.projectName || r.projectId);
  return [...m].map(([id, ten]) => ({ id, ten })).sort((a, b) => a.ten.localeCompare(b.ten, 'vi'));
}

// ─────────────── Gom số ───────────────

export interface DongLoiGom {
  nhan: string;
  loai: LoaiLoi;
  nhom: string;
  /** Mức nặng nhất trong các sự cố thuộc lỗi này. */
  mucDo: MucDo;
  soLan: number;
  /** Lần gần nhất (chuỗi gốc). */
  ganNhat: string;
  soLuong: number;
}

const nangHon = (a: MucDo, b: MucDo): MucDo => (MUC_DO_SU_CO.indexOf(a) <= MUC_DO_SU_CO.indexOf(b) ? a : b);

/** Gom sự cố theo nhãn lỗi, nhiều lần nhất trước rồi theo nhãn. */
export function gomTheoLoi(ds: readonly SuCo[], nhanCua: (r: SuCo) => string, chuaRo: string): DongLoiGom[] {
  const nhom = new Map<string, DongLoiGom & { luong: Set<string> }>();
  for (const r of ds) {
    const nhan = nhanCua(r) || chuaRo;
    const muc = mucDoSuCo(r);
    const luc = r.createdAt ?? '';
    const co = nhom.get(nhan);
    if (!co) {
      nhom.set(nhan, {
        nhan, loai: loaiSuCo(r), nhom: r.errorGroup ?? '', mucDo: muc, soLan: 1, ganNhat: luc, soLuong: 0,
        luong: new Set([r.workflowId || r.name]),
      });
      continue;
    }
    co.soLan += 1;
    co.mucDo = nangHon(co.mucDo, muc);
    if (ms(luc) > ms(co.ganNhat)) co.ganNhat = luc;
    co.luong.add(r.workflowId || r.name);
  }
  return [...nhom.values()]
    .map(({ luong, ...d }) => ({ ...d, soLuong: luong.size }))
    .sort((a, b) => b.soLan - a.soLan || a.nhan.localeCompare(b.nhan, 'vi'));
}

export function demTheoLoai(ds: readonly DongLoiGom[]): Record<LoaiLoi, number> {
  return {
    foreseen: ds.filter((d) => d.loai === 'foreseen').length,
    unforeseen: ds.filter((d) => d.loai === 'unforeseen').length,
  };
}

/** Đếm theo một nhãn, giảm dần. */
export function demTheo(ds: readonly SuCo[], khoa: (r: SuCo) => string, chuaRo: string): { nhan: string; so: number }[] {
  const dem = new Map<string, number>();
  for (const r of ds) {
    const k = khoa(r) || chuaRo;
    dem.set(k, (dem.get(k) ?? 0) + 1);
  }
  return [...dem].map(([nhan, so]) => ({ nhan, so })).sort((a, b) => b.so - a.so);
}

/** Một cột/thanh xếp chồng: số sự cố của từng nguồn, thứ tự `NGUON_SU_CO`. */
export interface CotNguon {
  khoa: string;
  /** Ngày `dd/MM`, giờ `0..23`, hoặc tên Project. */
  nhan: string;
  lat: number[];
}

const rong = (): number[] => NGUON_SU_CO.map(() => 0);
const hai = (n: number) => String(n).padStart(2, '0');
const khoaNgay = (d: Date) => `${d.getFullYear()}-${hai(d.getMonth() + 1)}-${hai(d.getDate())}`;

/** Mọi ngày trong khoảng (cả ngày không có sự cố), tăng dần, theo giờ máy người xem. */
export function theoNgay(ds: readonly SuCo[], soNgay: number, bayGio: number): CotNguon[] {
  const homNay = new Date(bayGio);
  homNay.setHours(0, 0, 0, 0);
  const cot = Array.from({ length: soNgay }, (_, i) => {
    const d = new Date(homNay);
    d.setDate(homNay.getDate() - (soNgay - 1 - i));
    return { khoa: khoaNgay(d), nhan: `${hai(d.getDate())}/${hai(d.getMonth() + 1)}`, lat: rong() };
  });
  const vt = new Map(cot.map((c, i) => [c.khoa, i]));
  for (const r of ds) {
    const t = ms(r.createdAt);
    const i = t ? vt.get(khoaNgay(new Date(t))) : undefined;
    if (i !== undefined) cot[i]!.lat[vtNguon(r)]! += 1;
  }
  return cot;
}

export function theoGio(ds: readonly SuCo[]): CotNguon[] {
  const cot = Array.from({ length: 24 }, (_, h) => ({ khoa: String(h), nhan: `${h}h`, lat: rong() }));
  for (const r of ds) {
    const t = ms(r.createdAt);
    if (t) cot[new Date(t).getHours()]!.lat[vtNguon(r)]! += 1;
  }
  return cot;
}

const SO_PROJECT_TOI_DA = 8;
export const KHOA_PROJECT_KHAC = '__khac__';

/** Theo Project, giảm dần; ngoài 8 Project đầu gộp vào một dòng "Khác". */
export function theoProject(ds: readonly SuCo[], chuaRo: string, khac: string): CotNguon[] {
  const m = new Map<string, CotNguon>();
  for (const r of ds) {
    const khoa = r.projectId || r.projectName || chuaRo;
    const nhan = r.projectName && r.projectId ? `${r.projectName} · ${r.projectId}` : r.projectName || r.projectId || chuaRo;
    const d = m.get(khoa) ?? { khoa, nhan, lat: rong() };
    d.lat[vtNguon(r)]! += 1;
    m.set(khoa, d);
  }
  const tong = (d: CotNguon) => d.lat.reduce((a, v) => a + v, 0);
  const xep = [...m.values()].sort((a, b) => tong(b) - tong(a));
  if (xep.length <= SO_PROJECT_TOI_DA) return xep;
  const gop: CotNguon = { khoa: KHOA_PROJECT_KHAC, nhan: khac, lat: rong() };
  for (const d of xep.slice(SO_PROJECT_TOI_DA)) d.lat.forEach((v, i) => (gop.lat[i]! += v));
  return [...xep.slice(0, SO_PROJECT_TOI_DA), gop];
}
